import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect, useParams } from 'react-router-dom';
import { getProvider } from '../config/providers';
import useMarketplaceSession from '../hooks/useMarketplaceSession';
import { probe } from '../hooks/marketplaceSessionStore';
import { ensureAccountDetails } from '../hooks/accountDetails';
import MarketplaceDisconnectControl from './MarketplaceDisconnectControl';
import MarketplaceResolving from './MarketplaceResolving';

import '../marketplace.css';

const APPLICATION_START_PATH = '/onboarding/merchant/';

export default function Connected() {
	const { provider: providerKey } = useParams();
	const provider = getProvider(providerKey);

	const session = useMarketplaceSession();

	const [prefill, setPrefill] = useState('idle');  // 'idle' -> 'loading' -> 'ready'

	useEffect(() => {
		probe(providerKey);
	}, [providerKey]);

	const needsPrefill = session.connected && !session.hasOpportunity;

	useEffect(() => {
		if (!needsPrefill || prefill !== 'idle') return;

		setPrefill('loading');
		ensureAccountDetails(providerKey).then(() => setPrefill('ready'));
	}, [needsPrefill, prefill, providerKey]);

	if (!session.confirmed) {
		return (
			<MarketplaceResolving
				provider={provider}
				message={`Checking your ${provider.label} connection…`}
			/>
		);
	}

	if (!session.signedIn || !session.connected) {
		return <Redirect to={`/marketplace/${provider.key}/connect`} />;
	}

	const alreadyApplied = session.hasOpportunity;

	if (alreadyApplied) {
		return (
			<>
				<Helmet>
					<meta name="referrer" content="no-referrer" />
					<title>Already Connected | Uplyft Capital</title>
				</Helmet>
				<main className="mp-page" role="main">
					<div className="container">
						<div className="mp-card">
							<div className="mp-icon">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M5 13l4 4L19 7" stroke="#927DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</div>

							<h1>Good news — we already have your information</h1>
							<p className="mp-subhead">
								We already have your information on file from your connected {provider.label} account.
								Your Uplyft Capital representative will be in touch shortly.
							</p>

							<MarketplaceDisconnectControl provider={provider} session={session} />

							<div className="mp-help">
								<h2>Have questions?</h2>
								<p>
									Feel free to call us, we&apos;ll be happy to assist you.{' '}
									<a href="tel:1 800 515-7531"><strong>1 (800) 515-7531</strong></a>
								</p>
							</div>
						</div>
					</div>
				</main>
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>{provider.label} Connected | Uplyft Capital</title>
			</Helmet>
			<main className="mp-page" role="main">
				<div className="container">
					<div className="mp-card">
						<div className="mp-icon">
							<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M5 13l4 4L19 7" stroke="#927DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</div>

						<h1>{provider.label} connected</h1>
						<p className="mp-subhead">
							We&apos;ve pulled in your business details from {provider.label}. Just a few more
							questions and your application is on its way to an Uplyft Capital representative.
						</p>

						<div className="mp-buttons">
							{prefill === 'loading' ? (
								<span className="mp-cta mp-cta--busy" role="status" aria-live="polite">
									Preparing your details…
								</span>
							) : (
								<Link
									to={APPLICATION_START_PATH}
									className="mp-cta"
									style={{ background: provider.brandColor }}
								>
									Verify Final Details
								</Link>
							)}
						</div>

						<MarketplaceDisconnectControl provider={provider} session={session} />
					</div>
				</div>
			</main>
		</>
	);
}
